import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { ReportEditableFields } from 'fuel-benchmark-contracts';
import { Car, GitCompare, Plus, Save, Trash2 } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { orpc } from '../../apis/orpc-query';
import { Button } from '../../components/ui/Button';
import { FormField } from '../../components/ui/FormField';
import { Input } from '../../components/ui/Input';
import { Spinner } from '../../components/ui/Spinner';
import { Textarea } from '../../components/ui/Textarea';
import { serverMessage, submitErrorKey } from '../../lib/form-helpers';
import { useI18nTranslation } from '../../lib/use-i18n-translation';
import { scenarioSummary } from '../compare/compare-display';
import { MAX_COMPARE_VEHICLES, parseVehicleIds, serializeVehicleIds, toggleVehicleId } from '../compare/compare-selection';
import { VehicleSelectTable } from '../compare/VehicleSelectTable';
import { ReportDocument } from './ReportDocument';
import {
  addableScenarios,
  addTestTo,
  builderStep,
  MAX_CONCLUSIONS,
  MAX_TESTS,
  refreshUntouchedEdits,
  removeTestAt,
  resolveEdits,
  runSelectionOf,
  sameTests,
  seedTests,
  withEdits,
  withTestRuns,
  type BuilderTest,
} from './report-display';
import { ReportTestCard } from './ReportTestCard';

/** Long enough that unchecking three runs in a row is one request, short enough to feel immediate. */
const TESTS_DEBOUNCE_MS = 400;

const CATALOGUE_PAGE_SIZE = 200;

/**
 * The report builder (plan §7 `/reports/new`, §8), in three steps top to bottom: the **vehicles** every test
 * compares, the **tests** the report spans, and then the live document beside the only texts a human may
 * change — the title, the executive takeaway and a list of nought to six conclusions. Everything else is
 * computed by `reports.generate` and recomputed by `reports.save`, so the preview and the saved report can
 * never disagree about a number.
 *
 * Both entries land here. The reports list links straight to `/reports/new`, and the builder then opens on its
 * own vehicle step with an empty test list that the "Add a test" strip fills. The compare screen's "Generate
 * report" hands over the vehicles and the one scenario it compared in the URL, with that comparison's run
 * selection in router state, so TEST 1 arrives pre-filled — and the strip still offers every other scenario
 * these vehicles have proven. Either way the vehicles live in the URL, so the builder stays a shareable link.
 */
export const ReportBuilderPage = () => {
  const { t } = useI18nTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();

  const [vehicleIds, setVehicleIds] = useState<string[]>(() => parseVehicleIds(searchParams.get('vehicleIds')));
  /** The scenario the compare hand-off named, kept only so a browser reload re-seeds the very same TEST 1. */
  const [seedScenarioId, setSeedScenarioId] = useState<string>(() => searchParams.get('scenarioId') ?? '');
  const [tests, setTests] = useState<BuilderTest[]>(() => seedTests(searchParams.get('scenarioId') ?? '', location.state));
  const [debouncedTests, setDebouncedTests] = useState<BuilderTest[]>(tests);
  const [edits, setEdits] = useState<ReportEditableFields | null>(null);
  /** The generated texts the editors currently hold, so a field the engineer typed in can be told apart. */
  const [seeded, setSeeded] = useState<ReportEditableFields | null>(null);
  /** The vehicle table is a step, not a permanent fixture: once two are picked it collapses to a summary. */
  const [isPickingVehicles, setIsPickingVehicles] = useState(false);

  const step = builderStep(vehicleIds, tests);
  const hasVehicles = step !== 'VEHICLES';

  // The URL is the source of truth for the hand-off, so a deep link and a click produce one state.
  useEffect(() => {
    const next = new URLSearchParams();

    if (vehicleIds.length > 0) {
      next.set('vehicleIds', serializeVehicleIds(vehicleIds));
    }

    if (seedScenarioId !== '') {
      next.set('scenarioId', seedScenarioId);
    }

    if (next.toString() !== searchParams.toString()) {
      setSearchParams(next, { replace: true });
    }
  }, [vehicleIds, seedScenarioId, searchParams, setSearchParams]);

  // Only the settled test list is sent, so unchecking several runs in a row is one generate call.
  useEffect(() => {
    if (sameTests(tests, debouncedTests)) {
      return;
    }

    const timer = window.setTimeout(() => setDebouncedTests(tests), TESTS_DEBOUNCE_MS);

    return () => window.clearTimeout(timer);
  }, [tests, debouncedTests]);

  const catalogue = useQuery(
    orpc.vehicles.list.queryOptions({
      input: { page: 1, pageSize: CATALOGUE_PAGE_SIZE, sortBy: 'code', sortDirection: 'asc', status: 'ACTIVE' },
    }),
  );

  const common = useQuery(orpc.compare.commonScenarios.queryOptions({ input: { vehicleIds }, enabled: hasVehicles }));

  const draft = useQuery(
    orpc.reports.generate.queryOptions({
      input: { vehicleIds, tests: debouncedTests.map((test) => ({ scenarioId: test.scenarioId, runSelection: runSelectionOf(test) })) },
      enabled: hasVehicles && debouncedTests.length > 0,
      // A refused selection (a scenario a vehicle has never proven) is an answer, not a glitch worth retrying.
      retry: false,
    }),
  );

  /**
   * The generated texts seed the editors, and re-seed the fields nobody has typed in whenever the document is
   * regenerated — adding a test rewrites what the generator would propose, and a takeaway written about one
   * test has no business standing over a brief that now spans two. Anything the engineer edited is theirs.
   */
  useEffect(() => {
    const generated = draft.data?.editable;

    if (!generated) {
      return;
    }

    setEdits((current) => (current === null ? generated : refreshUntouchedEdits(current, seeded, generated)));
    setSeeded(generated);
  }, [draft.data, seeded]);

  const save = useMutation(
    orpc.reports.save.mutationOptions({
      onSuccess: async ({ id }: { id: string }) => {
        await queryClient.invalidateQueries({ queryKey: orpc.reports.list.key() });
        navigate(`/reports/${id}`);
      },
    }),
  );

  const preview = useMemo(() => (draft.data ? withEdits(draft.data.snapshot, draft.data.editable, edits) : null), [draft.data, edits]);

  const onSave = (): void => {
    if (!draft.data) {
      return;
    }

    save.mutate({
      vehicleIds,
      tests: debouncedTests.map((test) => ({ scenarioId: test.scenarioId, runSelection: runSelectionOf(test) })),
      ...resolveEdits(draft.data.editable, edits),
    });
  };

  const setField = (field: 'title' | 'takeaway', value: string): void =>
    setEdits((current) => (current === null ? current : { ...current, [field]: value }));

  const setConclusion = (index: number, value: string): void =>
    setEdits((current) =>
      current === null ? current : { ...current, conclusions: current.conclusions.map((text, position) => (position === index ? value : text)) },
    );

  // The conclusion list is the human's, not the generator's: a row can be removed outright, and an added
  // row starts empty. Both are applied to the preview immediately, and a row left empty is dropped on save.
  const removeConclusion = (index: number): void =>
    setEdits((current) =>
      current === null ? current : { ...current, conclusions: current.conclusions.filter((_, position) => position !== index) },
    );

  const addConclusion = (): void =>
    setEdits((current) =>
      current === null || current.conclusions.length >= MAX_CONCLUSIONS ? current : { ...current, conclusions: [...current.conclusions, ''] },
    );

  /**
   * Every test of a report compares the same columns, so a scenario shortlist only means anything for one
   * vehicle set — changing the vehicles drops the tests picked for the old one, hand-off seed included.
   */
  const onToggleVehicle = (vehicleId: string): void => {
    setVehicleIds((current) => toggleVehicleId(current, vehicleId));
    setSeedScenarioId('');
    setTests([]);
  };

  const addTest = (nextScenarioId: string): void => setTests((current) => addTestTo(current, nextScenarioId));

  const removeTest = (index: number): void => setTests((current) => removeTestAt(current, index));

  const setTestRuns = (index: number, runIds: Record<string, string[]>): void => setTests((current) => withTestRuns(current, index, runIds));

  const addable = addableScenarios(common.data?.scenarios ?? [], tests);
  const atCap = tests.length >= MAX_TESTS;
  const draftError = draft.isError ? (serverMessage(draft.error) ?? t('errors.loadFailed')) : null;
  const isRegenerating = draft.isFetching && draft.data !== undefined;
  const showVehicleTable = !hasVehicles || isPickingVehicles;
  const chosenVehicles = vehicleIds.map((vehicleId) => (catalogue.data?.items ?? []).find((vehicle) => vehicle.id === vehicleId));

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-foreground">{t('reports.builder.title')}</h1>
          <p className="mt-1 max-w-3xl text-sm text-muted-foreground">{t('reports.builder.subtitle')}</p>
        </div>
        <Button
          variant="outline"
          onClick={() => navigate('/compare')}
        >
          <GitCompare className="size-4" />
          {t('reports.builder.backToCompare')}
        </Button>
      </div>

      <section
        className="space-y-3"
        data-report-vehicles
      >
        <div className="flex flex-wrap items-baseline justify-between gap-2">
          <h2 className="text-sm font-medium text-foreground">{t('reports.builder.vehicles')}</h2>
          <p className="text-xs text-muted-foreground">{t('compare.vehicles.counter', { count: vehicleIds.length, max: MAX_COMPARE_VEHICLES })}</p>
        </div>
        <p className="max-w-3xl text-xs text-muted-foreground">{t('reports.builder.vehiclesHint')}</p>

        {showVehicleTable ? (
          <VehicleSelectTable
            vehicles={catalogue.data?.items ?? []}
            selectedIds={vehicleIds}
            onToggle={onToggleVehicle}
            isLoading={catalogue.isPending}
          />
        ) : (
          <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-border bg-card p-4">
            <ul className="flex min-w-0 flex-wrap items-center gap-x-4 gap-y-1">
              {chosenVehicles.map((vehicle, index) => (
                <li
                  key={vehicle?.id ?? index}
                  className="flex items-baseline gap-2 text-sm text-card-foreground"
                >
                  <Car className="size-3.5 shrink-0 text-muted-foreground" />
                  {vehicle ? `${vehicle.manufacturer} ${vehicle.model} ${vehicle.modelYear}` : t('app.loading')}
                  <span className="font-mono text-xs text-muted-foreground">{vehicle?.code ?? ''}</span>
                </li>
              ))}
            </ul>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => setIsPickingVehicles(true)}
            >
              {t('reports.builder.changeVehicles')}
            </Button>
          </div>
        )}

        {!hasVehicles ? <p className="text-sm text-muted-foreground">{t('reports.builder.needVehicles')}</p> : null}

        {hasVehicles && isPickingVehicles ? (
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => setIsPickingVehicles(false)}
          >
            {t('reports.builder.doneVehicles')}
          </Button>
        ) : null}

        {common.data?.crossSegment ? (
          <p
            role="status"
            className="rounded-md border px-3 py-2 text-sm"
            style={{ color: '#a16207', borderColor: '#a1620733' }}
          >
            {t('compare.vehicles.crossSegment', {
              segments: [...new Set((common.data?.vehicles ?? []).map((vehicle) => vehicle.segmentName))].join(', '),
            })}
          </p>
        ) : null}
      </section>

      {hasVehicles ? (
        <>
          <section
            className="space-y-3"
            data-report-tests
          >
            <div className="flex flex-wrap items-baseline justify-between gap-2">
              <h2 className="text-sm font-medium text-foreground">{t('reports.builder.tests')}</h2>
              <p className="text-xs text-muted-foreground">{t('reports.builder.testsCounter', { count: tests.length, max: MAX_TESTS })}</p>
            </div>
            <p className="max-w-3xl text-xs text-muted-foreground">{t('reports.builder.testsHint')}</p>

            {tests.map((test, index) => (
              <ReportTestCard
                key={test.scenarioId}
                index={index}
                vehicleIds={vehicleIds}
                test={test}
                canRemove
                onChange={(runIds) => setTestRuns(index, runIds)}
                onRemove={() => removeTest(index)}
              />
            ))}

            <div className="rounded-lg border border-dashed border-border bg-card p-4">
              <h3 className="text-sm font-medium text-card-foreground">
                {tests.length === 0 ? t('reports.builder.firstTest') : t('reports.builder.addTest')}
              </h3>
              <p className="mt-1 text-xs text-muted-foreground">
                {atCap ? t('reports.builder.maxTests', { max: MAX_TESTS }) : t('reports.builder.addTestHint')}
              </p>

              {common.isPending ? (
                <div className="mt-3 flex items-center gap-2 text-xs text-muted-foreground">
                  <Spinner label={t('app.loading')} />
                  {t('app.loading')}
                </div>
              ) : null}

              {common.isError ? (
                <p
                  role="alert"
                  className="mt-3 rounded-md border border-destructive/40 bg-destructive/5 px-3 py-2 text-xs text-destructive"
                >
                  {serverMessage(common.error) ?? t('errors.loadFailed')}
                </p>
              ) : null}

              {common.isSuccess && addable.length === 0 && !atCap ? (
                <p className="mt-3 text-xs text-muted-foreground">
                  {tests.length === 0 ? t('reports.builder.noTestsAvailable') : t('reports.builder.noMoreTests')}
                </p>
              ) : null}

              {addable.length > 0 ? (
                <div className="mt-3 flex flex-wrap gap-2">
                  {addable.map((entry) => (
                    <Button
                      key={entry.scenario.id}
                      type="button"
                      variant="outline"
                      size="sm"
                      data-add-scenario={entry.scenario.code}
                      onClick={() => addTest(entry.scenario.id)}
                    >
                      <Plus className="size-3.5" />
                      {scenarioSummary(entry.scenario, t)}
                      <span className="font-mono text-xs text-muted-foreground">{entry.scenario.code}</span>
                    </Button>
                  ))}
                </div>
              ) : null}
            </div>
          </section>

          {tests.length === 0 ? (
            <p className="rounded-lg border border-border bg-card px-4 py-6 text-sm text-muted-foreground">{t('reports.builder.noTests')}</p>
          ) : draftError ? (
            <p
              role="alert"
              className="rounded-md border border-destructive/40 bg-destructive/5 px-3 py-2 text-sm text-destructive"
            >
              {draftError}
            </p>
          ) : draft.isPending ? (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Spinner label={t('app.loading')} />
              {t('reports.builder.generating')}
            </div>
          ) : preview && edits ? (
            <div className="grid items-start gap-6 xl:grid-cols-[minmax(0,1fr)_360px]">
              <section className="min-w-0 space-y-2">
                <div className="flex flex-wrap items-baseline justify-between gap-2">
                  <h2 className="text-sm font-medium text-foreground">{t('reports.builder.preview')}</h2>
                  {isRegenerating ? (
                    <span className="flex items-center gap-2 text-xs text-muted-foreground">
                      <Spinner label={t('app.loading')} />
                      {t('reports.builder.regenerating')}
                    </span>
                  ) : null}
                </div>
                <p className="text-xs text-muted-foreground">{t('reports.builder.previewHint')}</p>
                <div className={`overflow-x-auto rounded-lg border border-border bg-muted/40 p-4 ${isRegenerating ? 'opacity-60' : ''}`}>
                  <ReportDocument snapshot={preview} />
                </div>
              </section>

              <section className="space-y-4 rounded-lg border border-border bg-card p-4 xl:sticky xl:top-6">
                <div>
                  <h2 className="text-sm font-medium text-card-foreground">{t('reports.builder.editPanel')}</h2>
                  <p className="mt-1 text-xs text-muted-foreground">{t('reports.builder.editHint')}</p>
                </div>

                <FormField
                  id="report-title"
                  label={t('reports.fields.title')}
                >
                  <Input
                    id="report-title"
                    value={edits.title}
                    onChange={(event) => setField('title', event.target.value)}
                  />
                </FormField>

                <FormField
                  id="report-takeaway"
                  label={t('reports.fields.takeaway')}
                  hint={t('reports.builder.blankHint')}
                >
                  <Textarea
                    id="report-takeaway"
                    rows={4}
                    value={edits.takeaway}
                    onChange={(event) => setField('takeaway', event.target.value)}
                  />
                </FormField>

                <div className="space-y-3 border-t border-border pt-3">
                  <div>
                    <h3 className="text-sm font-medium text-card-foreground">{t('reports.builder.conclusions')}</h3>
                    <p className="mt-1 text-xs text-muted-foreground">{t('reports.builder.conclusionsHint')}</p>
                  </div>

                  {edits.conclusions.length === 0 ? <p className="text-xs text-muted-foreground">{t('reports.builder.noConclusions')}</p> : null}

                  {edits.conclusions.map((conclusion, index) => (
                    <div
                      key={index}
                      className="space-y-1.5"
                      data-conclusion-row={index}
                    >
                      <div className="flex items-center justify-between gap-2">
                        <label
                          htmlFor={`report-conclusion-${index}`}
                          className="text-xs font-medium text-card-foreground"
                        >
                          {t('reports.fields.conclusion', { number: String(index + 1).padStart(2, '0') })}
                        </label>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          aria-label={t('reports.builder.removeConclusion', { number: String(index + 1).padStart(2, '0') })}
                          onClick={() => removeConclusion(index)}
                        >
                          <Trash2 className="size-3.5" />
                          {t('actions.remove')}
                        </Button>
                      </div>
                      <Textarea
                        id={`report-conclusion-${index}`}
                        rows={4}
                        value={conclusion}
                        onChange={(event) => setConclusion(index, event.target.value)}
                      />
                    </div>
                  ))}

                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="w-full"
                    disabled={edits.conclusions.length >= MAX_CONCLUSIONS}
                    onClick={addConclusion}
                  >
                    <Plus className="size-3.5" />
                    {t('reports.builder.addConclusion')}
                  </Button>
                </div>

                {save.isError ? (
                  <p
                    role="alert"
                    className="rounded-md border border-destructive/40 bg-destructive/5 px-3 py-2 text-xs text-destructive"
                  >
                    {serverMessage(save.error) ?? t(submitErrorKey(save.error))}
                  </p>
                ) : null}

                <div className="space-y-2 border-t border-border pt-3">
                  <Button
                    className="w-full"
                    onClick={onSave}
                    isLoading={save.isPending}
                  >
                    <Save className="size-4" />
                    {t('reports.builder.save')}
                  </Button>
                  <p className="text-xs text-muted-foreground">{t('reports.builder.saveHint')}</p>
                </div>
              </section>
            </div>
          ) : null}
        </>
      ) : null}
    </div>
  );
};
